import usePrivateLink from "../config/usePrivateLink";
import React, { useEffect, useState } from "react";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import { Link } from "react-router-dom";
import {
  closeModal,
  formatDate,
  formatTime,
  ToastMain,
  SweetAlertShow,
  LoadingDiv,
} from "../master/Master";
import { getSessions } from "../../api/SessionsService";
import Swal from "sweetalert2";
import {
  rejoinStaff,
  StaffList,
  terminateStaff,
  workingSchedulesList,
  salaryPreviewByDate,
  markStaffAsDeleted,
} from "../../api/Staff";
import StaffLayout from "./StaffLayout";
import { imgBaseUrl } from "../../api/api";
import DownloadExcel from "../master/DownloadExcel";
import * as XLSX from "xlsx";

const rupee = (n) =>
  "₹" +
  parseFloat(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const dayStatusBadge = (status) => {
  const map = {
    Present: "bg-label-success",
    Absent: "bg-label-danger",
    Leave: "bg-label-warning",
    Holiday: "bg-label-info",
  };
  return (
    <span className={`badge ${map[status] || "bg-label-secondary"} px-3`}>
      {status || "—"}
    </span>
  );
};

export default function Staffs() {
  const hasAccess = usePrivateLink();
  const [loading, setLoading] = useState(false);
  const [staffs, setStaffs] = useState("");
  const [selectedType, setSelectedType] = useState("1");
  const [filters, setFilters] = useState({
    search: "",
    staff_type: "",
  });
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [modalData, setModalData] = useState("");
  const [workingSchedules, setWorkingSchedules] = useState([]);

  // ── Day-wise salary preview modal state ─────────────────────────────────
  const [daySalaryStaff, setDaySalaryStaff] = useState(null);
  const [daySalaryDate, setDaySalaryDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [daySalaryResult, setDaySalaryResult] = useState(null);
  const [daySalaryLoading, setDaySalaryLoading] = useState(false);
  const [daySalaryError, setDaySalaryError] = useState("");

  const [permissions, setPermissions] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const [viewMenuStaff, setViewMenuStaff] = useState(null);

  const hasAssignedMenus = (menuGroup) => {
    if (!Array.isArray(menuGroup)) return false;

    return menuGroup.some(
      (group) => Array.isArray(group?.items) && group.items.length > 0,
    );
  };

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("user_data") || "{}");

    setIsSuperAdmin(userData?.role_id == 1);
  }, []);

  const getWorkingSchedule = async () => {
    setLoading(true);
    try {
      const response = await workingSchedulesList("");

      if (response?.data?.status) {
        setWorkingSchedules(response?.data?.data);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message ?? err?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getStaffs();
  }, [selectedType, filters.search, filters.staff_type]);

  useEffect(() => {
    getWorkingSchedule();
  }, []);

  const getStaffs = async () => {
    const addUrl = `&search=${filters.search || ""}&status=${selectedType}&staff_type=${filters.staff_type || ""}`;
    setLoading(true);
    try {
      const response = await StaffList(addUrl);
      if (response.data.status) {
        setStaffs(response?.data);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleStaffTerminate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData(e.target);
      const response = await terminateStaff(formData);
      if (response?.data?.status) {
        closeModal("staff_inactive");
        e.target.reset();
        ToastMain.fire({
          icon: "success",
          title: response?.data?.message || "something went wrong",
        }).then(() => {
          getStaffs();
        });
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      ToastMain.fire({
        icon: "error",
        title: error?.response?.data?.message || "something went wrong",
      });

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleStaffRejoin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData(e.target);
      const response = await rejoinStaff(formData);
      if (response?.data?.status) {
        closeModal("staff_rejoin");
        e.target.reset();
        ToastMain.fire({
          icon: "success",
          title: response?.data?.message || "Successfully",
        }).then(() => {
          getStaffs();
        });
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      ToastMain.fire({
        icon: "error",
        title: error?.response?.data?.message || "something went wrong",
      });

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDisableStaff = async (item) => {
    const ok = await Swal.fire({
      title: "Disable Staff?",
      html: `Are you sure you want to disable <b>${item?.name}</b>?<br/><small class="text-muted">This marks the staff record as deleted.</small>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Disable",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
    });
    if (!ok?.isConfirmed) return;

    setLoading(true);
    try {
      const res = await markStaffAsDeleted({ id: item.id });
      if (res?.data?.status) {
        ToastMain.fire({
          icon: "success",
          title: res?.data?.message || "Staff disabled successfully.",
        });
        getStaffs();
      } else {
        SweetAlertShow("info", "Warning", res?.data?.message);
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message || err?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    try {
      const rows = staffs?.data || [];

      if (!rows.length) {
        SweetAlertShow("info", "Info", "No staff records found.");
        return;
      }

      const exportData = rows.map((staff, index) => ({
        "S.No": index + 1,
        "Registration No": staff?.registration_no || "",
        "Staff Name": staff?.name || "",
        "Staff Type": staff?.staff_type || staff?.type_id || "",
        Mobile: staff?.mobile || "",
        Email: staff?.email || "",
        "Joining Date": staff?.employment_info?.joining_date
          ? formatDate(staff.employment_info.joining_date)
          : "",
        Gender: staff?.gender || "",
        Status:
          staff?.status == "1"
            ? "Active"
            : staff?.status == "2"
              ? "Inactive"
              : staff?.status || "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);

      worksheet["!cols"] = [
        { wch: 8 },
        { wch: 20 },
        { wch: 25 },
        { wch: 20 },
        { wch: 16 },
        { wch: 30 },
        { wch: 18 },
        { wch: 12 },
        { wch: 12 },
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Staff List");

      const fileName = `Staff_List_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`;

      XLSX.writeFile(workbook, fileName);

      ToastMain.fire({
        icon: "success",
        title: `${exportData.length} staff records exported successfully.`,
      });
    } catch (error) {
      console.error("Staff Excel Export Error:", error);

      SweetAlertShow(
        "error",
        "Error",
        error?.message || "Failed to export staff list.",
      );
    }
  };

  const handlePrint = () => {
    const printContent = document.getElementById("printTable");

    if (!printContent) {
      return;
    }

    const printWindow = window.open("", "_blank", "width=1200,height=800");

    printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>Staff List</title>

      <style>
        @page {
          size: A4 landscape;
          margin: 10mm;
        }

        * {
          box-sizing: border-box;
        }

        body {
          font-family: Arial, sans-serif;
          margin: 0;
          padding: 20px;
          color: #000;
        }

        h2 {
          margin-bottom: 20px;
          text-align: center;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px;
        }

        th,
        td {
          border: 1px solid #ccc;
          padding: 8px;
          text-align: left;
          vertical-align: middle;
        }

        th {
          background: #f2f2f2;
          font-weight: bold;
        }

        img {
          max-width: 40px;
          max-height: 40px;
        }

        .badge {
          padding: 4px 8px;
          border: 1px solid #999;
          border-radius: 4px;
        }

        .btn,
        button,
        a {
          display: none !important;
        }

        @media print {
          body {
            padding: 0;
          }

          table {
            page-break-inside: auto;
          }

          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }

          thead {
            display: table-header-group;
          }
        }
      </style>
    </head>

    <body>

      <h2>Staff List</h2>

      ${printContent.innerHTML}

    </body>
    </html>
  `);

    printWindow.document.close();

    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  // ── Day-wise salary preview: open modal, reset state for the clicked staff ──
  const openDaySalaryModal = (item) => {
    setDaySalaryStaff(item);
    setDaySalaryDate(new Date().toISOString().split("T")[0]);
    setDaySalaryResult(null);
    setDaySalaryError("");
  };

  // ── Day-wise salary preview: fetch (preview only — nothing is saved) ────
  const handleFetchDaySalary = async () => {
    if (!daySalaryStaff?.id) return;
    if (!daySalaryDate) {
      return SweetAlertShow("info", "Info", "Please select a date.");
    }

    setDaySalaryLoading(true);
    setDaySalaryError("");
    setDaySalaryResult(null);
    try {
      const res = await salaryPreviewByDate({
        staff_id: daySalaryStaff.id,
        date: daySalaryDate,
      });

      if (res?.data?.status) {
        const row = (res.data.data || [])[0] || null;
        if (!row) {
          setDaySalaryError("No data returned for this date.");
        } else if (row.skippable) {
          setDaySalaryError(
            row.skip_reason || "This date could not be previewed.",
          );
        } else {
          setDaySalaryResult(row);
        }
      } else {
        setDaySalaryError(res?.data?.message || "Something went wrong.");
      }
    } catch (err) {
      setDaySalaryError(
        err?.response?.data?.message || err?.message || "Something went wrong.",
      );
    } finally {
      setDaySalaryLoading(false);
    }
  };

  return (
    <>
      {loading && <LoadingDiv />}
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />
            <div className="content-wrapper">
              <div className="container-fluid mt-2">
                <div className="row">
                  <StaffLayout />
                  <div className="col-12">
                    <div className="card border-0 shadow-sm mb-4">
                      <div className="card-body">
                        {/* Header */}
                        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center mb-4">
                          <div>
                            <h3 className="mb-1 fw-bold text-primary">
                              <i className="ri-team-line me-2"></i>
                              Staff Management
                            </h3>
                            <p className="text-muted mb-0">
                              Manage all teaching and non-teaching staff.
                            </p>
                          </div>
                          <div className="d-flex mt-3 mt-lg-0">
                            {hasAccess("/staff/export") && (
                              <button
                                className="btn btn-success me-2"
                                title="Export Excel"
                                onClick={handleDownload}
                              >
                                <i className="ri-file-excel-2-line me-1"></i>
                                Export
                              </button>
                            )}
                            {hasAccess("/staff/print") && (
                              <button
                                className="btn btn-outline-primary me-2"
                                title="Print Staff List"
                                onClick={handlePrint}
                              >
                                <i className="ri-printer-line me-1"></i>
                                Print
                              </button>
                            )}
                            {hasAccess("/staff/add") && (
                              <Link to="/staff/add" className="btn btn-primary">
                                <i className="ri-user-add-line me-1"></i>
                                Add Staff
                              </Link>
                            )}
                          </div>
                        </div>
                        {/* Statistics */}
                        <div className="row g-3 mb-4">
                          <div className="col-md-4">
                            <div className="border rounded-3 p-3 h-100">
                              <small className="text-muted">Total Staff</small>
                              <h3 className="fw-bold mt-2 mb-0">
                                {staffs?.data?.length || 0}
                              </h3>
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="border rounded-3 p-3 h-100">
                              <small className="text-muted">Active</small>
                              <h3 className="fw-bold text-success mt-2 mb-0">
                                {staffs?.data?.filter((x) => x.status == "1")
                                  .length || 0}
                              </h3>
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="border rounded-3 p-3 h-100">
                              <small className="text-muted">Inactive</small>
                              <h3 className="fw-bold text-danger mt-2 mb-0">
                                {staffs?.data?.filter((x) => x.status != "1")
                                  .length || 0}
                              </h3>
                            </div>
                          </div>
                        </div>
                        {/* Search */}
                        <div className="row align-items-end">
                          <div className="col-lg-4">
                            <div className="form-floating form-floating-outline">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Search Staff"
                                value={filters.search}
                                onChange={(e) =>
                                  setFilters({
                                    ...filters,
                                    search: e.target.value,
                                  })
                                }
                              />
                              <label>
                                Search Name / Mobile / Registration No.
                              </label>
                            </div>
                          </div>

                          <div className="col-lg-3">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select"
                                value={filters.staff_type}
                                onChange={(e) =>
                                  setFilters({
                                    ...filters,
                                    staff_type: e.target.value,
                                  })
                                }
                              >
                                <option value="">Select Type</option>
                                <option value="Teaching">Teaching </option>
                                <option value="Administrative">
                                  Administrative
                                </option>
                                <option value="Other">Other </option>
                              </select>
                              <label>All Type</label>
                            </div>
                          </div>

                          <div className="col-lg-5 mt-3 mt-lg-0">
                            <div className="d-flex flex-wrap gap-2 justify-content-lg-end">
                              <button
                                className={`btn rounded-pill px-4 ${
                                  selectedType == ""
                                    ? "btn-primary"
                                    : "btn-outline-primary"
                                }`}
                                onClick={() => setSelectedType("")}
                              >
                                All
                              </button>
                              <button
                                className={`btn rounded-pill px-4 ${
                                  selectedType == "1"
                                    ? "btn-success"
                                    : "btn-outline-success"
                                }`}
                                onClick={() => setSelectedType("1")}
                              >
                                Active
                              </button>
                              <button
                                className={`btn rounded-pill px-4 ${
                                  selectedType == "2"
                                    ? "btn-danger"
                                    : "btn-outline-danger"
                                }`}
                                onClick={() => setSelectedType("2")}
                              >
                                Inactive
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="card">
                      <div className="card-body">
                        <div className="table-responsive" id="printTable">
                          <table className="table align-middle table-hover">
                            <thead className="table-light">
                              <tr>
                                <th width="60">#</th>
                                <th>Employee</th>
                                <th width="170">Contact</th>
                                <th width="170">Role</th>
                                <th>Joining Date</th>
                                <th>Status</th>
                                <th width="230" className="text-center">
                                  Actions
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {staffs?.data?.length > 0 ? (
                                staffs?.data?.map((item, i) => (
                                  <tr key={item.id || i}>
                                    <td className="fw-semibold text-muted">
                                      {i + 1}
                                    </td>
                                    <td>
                                      <div className="d-flex align-items-center">
                                        <div
                                          className="me-3 flex-shrink-0"
                                          style={{
                                            width: 48,
                                            height: 48,
                                          }}
                                        >
                                          {item?.img ? (
                                            <img
                                              src={`${imgBaseUrl}${item.img}`}
                                              alt={item.name}
                                              className="rounded-circle border"
                                              style={{
                                                width: "48px",
                                                height: "48px",
                                                objectFit: "cover",
                                              }}
                                            />
                                          ) : (
                                            <div
                                              className="rounded-circle bg-label-primary d-flex align-items-center justify-content-center fw-bold text-primary"
                                              style={{
                                                width: "48px",
                                                height: "48px",
                                                fontSize: "17px",
                                              }}
                                            >
                                              {item?.name
                                                ?.split(" ")
                                                ?.map((x) => x[0])
                                                ?.slice(0, 2)
                                                ?.join("")
                                                ?.toUpperCase()}
                                            </div>
                                          )}
                                        </div>
                                        <div>
                                          <div className="fw-semibold">
                                            {item?.name}
                                          </div>
                                          <small className="text-muted">
                                            Reg. No :{" "}
                                            {item?.registration_no || "-"}
                                          </small>
                                        </div>
                                      </div>
                                    </td>
                                    <td>
                                      <i className="ri-phone-line text-primary me-2"></i>
                                      {item?.mobile}
                                    </td>
                                    <td>
                                      <span className="badge bg-label-info rounded-pill px-3">
                                        {item?.staff_type}
                                      </span>
                                    </td>
                                    <td>
                                      <i className="ri-calendar-line text-success me-2"></i>
                                      {formatDate(
                                        item?.employment_info?.joining_date,
                                      )}
                                    </td>
                                    <td>
                                      {hasAccess("/staff/status/update") && (
                                        <span
                                          role="button"
                                          className={`badge rounded-pill px-3 py-2 ${
                                            item?.status == "1"
                                              ? "bg-label-success"
                                              : "bg-label-danger"
                                          }`}
                                          data-bs-toggle="modal"
                                          data-bs-target={
                                            item?.status == "1"
                                              ? "#staff_inactive"
                                              : "#staff_rejoin"
                                          }
                                          onClick={() => setModalData(item)}
                                        >
                                          <i
                                            className={`me-1 ${
                                              item?.status == "1"
                                                ? "ri-checkbox-circle-fill"
                                                : "ri-close-circle-fill"
                                            }`}
                                          ></i>
                                          {item?.status == "1"
                                            ? "Active"
                                            : "Inactive"}
                                        </span>
                                      )}
                                    </td>
                                    <td>
                                      <div className="d-flex justify-content-center gap-2">
                                        {hasAccess("/teachers/view/:id") && (
                                          <Link
                                            to={`/teachers/view/${item?.id}`}
                                            className="btn btn-sm btn-outline-primary"
                                            title="View Profile"
                                          >
                                            <i className="ri-eye-line"></i>
                                          </Link>
                                        )}
                                        {hasAccess("/staff/update") && (
                                          <Link
                                            to={`/staff/update?id=${item?.id}`}
                                            className="btn btn-sm btn-outline-warning"
                                            title="Edit Staff"
                                          >
                                            <i className="ri-pencil-line"></i>
                                          </Link>
                                        )}
                                        {isSuperAdmin &&
                                          hasAccess("/staff/day/salary") && (
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-outline-success"
                                              title="View Day-wise Salary"
                                              data-bs-toggle="modal"
                                              data-bs-target="#staff_day_salary"
                                              onClick={() =>
                                                openDaySalaryModal(item)
                                              }
                                            >
                                              <i className="ri-money-rupee-circle-line"></i>
                                            </button>
                                          )}
                                        {hasAccess("/staff/delete") &&
                                          isSuperAdmin && (
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-outline-danger"
                                              title="Disable Staff"
                                              onClick={() =>
                                                handleDisableStaff(item)
                                              }
                                            >
                                              <i className="ri-user-unfollow-line"></i>
                                            </button>
                                          )}

                                        {hasAssignedMenus(item?.menu_group) && (
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-outline-info"
                                            title="View Assigned Menus"
                                            onClick={() =>
                                              setViewMenuStaff(item)
                                            }
                                          >
                                            <i className="ri-menu-2-line"></i>
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="7">
                                    <div className="py-5 text-center">
                                      <div
                                        className="rounded-circle bg-label-secondary d-inline-flex align-items-center justify-content-center mb-3"
                                        style={{
                                          width: 80,
                                          height: 80,
                                        }}
                                      >
                                        <i
                                          className="ri-team-line"
                                          style={{
                                            fontSize: 34,
                                          }}
                                        ></i>
                                      </div>
                                      <h5 className="mb-1">No Staff Found</h5>
                                      <p className="text-muted mb-0">
                                        Try changing your search or filters.
                                      </p>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="staff_inactive"
        tabIndex={-1}
        aria-labelledby="exampleModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog">
          <div className="modal-content">
            <form onSubmit={handleStaffTerminate}>
              <div className="modal-header py-3 bg-label-primary">
                <h1 className="modal-title fs-5" id="exampleModalLabel">
                  Status Update
                </h1>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                />
              </div>
              <div className="modal-body">
                <div className="row g-4">
                  <input
                    type="hidden"
                    name="staff_id"
                    id="staff_id"
                    value={modalData?.id}
                  />

                  <div className="col-md-12">
                    <div className=" form-floating form-floating-outline ">
                      <select name="status" id="status" className="form-select">
                        <option value="4">Inactive</option>
                      </select>
                      <label htmlFor="status">Status</label>
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className=" form-floating form-floating-outline ">
                      <input
                        type="date"
                        className="form-control"
                        name="end_date"
                        id="end_date"
                        min={
                          modalData?.joining_date
                            ? new Date(modalData.joining_date)
                                .toISOString()
                                .split("T")[0]
                            : ""
                        }
                      />
                      <label htmlFor="end_date">End Date</label>
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className=" form-floating form-floating-outline ">
                      <textarea
                        type="text"
                        className="form-control input-uppercase"
                        name="reason"
                        id="reason"
                        placeholder="Reason"
                      />
                      <label htmlFor="reason">Reason</label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer py-3 bg-label-primary">
                <button
                  type="button"
                  className="btn btn-secondary"
                  data-bs-dismiss="modal"
                >
                  Close
                </button>
                <button type="submit" className="btn btn-primary">
                  Save changes
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="staff_rejoin"
        tabIndex={-1}
        aria-labelledby="exampleModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <form action="" onSubmit={handleStaffRejoin}>
              <div className="modal-header py-3 bg-label-primary">
                <h1 className="modal-title fs-5" id="exampleModalLabel">
                  Staff Rejoin
                </h1>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                />
              </div>
              <div className="modal-body">
                <div className="card border shadow-sm mb-4">
                  <div className="card-header bg-label-primary fw-semibold">
                    <i className="ri-user-settings-line me-2"></i>
                    Employment Details
                  </div>
                  <div className="card-body pt-4">
                    <div className="row g-3">
                      <input
                        type="hidden"
                        name="staff_id"
                        id="staff_id"
                        value={modalData?.id}
                      />
                      <div className="col-md-6">
                        <div className="form-floating form-floating-outline">
                          <input
                            type="date"
                            className="form-control"
                            name="joining_date"
                            value={modalData?.joining_date}
                            onChange={(e) =>
                              setModalData((prev) => ({
                                ...prev,
                                joining_date: e.target.value,
                              }))
                            }
                          />
                          <label>Rejoining Date</label>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className=" form-floating form-floating-outline ">
                          <select
                            name="salary_type"
                            id="salary_type"
                            className="form-control form-select"
                            value={modalData?.salary_type}
                            onChange={(e) =>
                              setModalData((prev) => ({
                                ...prev,
                                salary_type: e.target.value,
                              }))
                            }
                          >
                            <option value="" selected disabled>
                              Select Salary Type
                            </option>
                            <option value="Monthly">Monthly</option>
                            <option value="Hourly">Hourly</option>
                          </select>
                          <label>Salary Type</label>
                        </div>
                      </div>
                      {modalData?.salary_type == "Monthly" && (
                        <div className="col-md-6">
                          <div className=" form-floating form-floating-outline ">
                            <select
                              name="working_schedule_id"
                              id="working_schedule_id"
                              className="form-control form-select"
                              value={modalData?.working_schedule_id}
                              onChange={(e) => {
                                const selectedId = e.target.value;

                                const selectedSchedule = workingSchedules.find(
                                  (s) => String(s.id) === String(selectedId),
                                );

                                setModalData((prev) => ({
                                  ...prev,
                                  working_schedule_id: selectedId,
                                  total_hours_per_day:
                                    selectedSchedule?.total_hours_per_day || "",
                                }));
                              }}
                            >
                              <option value="" selected disabled>
                                Select Working Schedule
                              </option>
                              {workingSchedules?.map((schedule, i) => (
                                <option key={schedule.id} value={schedule.id}>
                                  {schedule.name}
                                  {" ("}
                                  {formatTime(schedule.start_time)}
                                  {" - "}
                                  {formatTime(schedule.end_time)}
                                  {")"}
                                </option>
                              ))}
                            </select>
                            <label>Working Schedule</label>
                          </div>
                        </div>
                      )}
                      <div className="col-md-6">
                        <div className=" form-floating form-floating-outline ">
                          <input
                            type="number"
                            className="form-control"
                            name="total_hours_per_day"
                            id="total_hours_per_day"
                            value={modalData?.total_hours_per_day}
                            onChange={(e) =>
                              setModalData((prev) => ({
                                ...prev,
                                total_hours_per_day: e.target.value,
                              }))
                            }
                            // readOnly={
                            //   modalData?.salary_type === "Monthly" &&
                            //   modalData?.working_schedule_id
                            // }
                            placeholder="Working Hours Per Day"
                          />
                          <label>Working Hours / Day</label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card border shadow-sm mb-4">
                  <div className="card-header bg-label-success fw-semibold">
                    <i className="ri-money-rupee-circle-line me-2"></i>
                    Salary Details
                  </div>
                  <div className="card-body pt-4">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <div className=" form-floating form-floating-outline ">
                          <input
                            type="number"
                            className="form-control"
                            name="current_salary"
                            id="current_salary"
                            placeholder="Salary"
                            value={modalData?.current_salary}
                            onChange={(e) =>
                              setModalData((prev) => ({
                                ...prev,
                                current_salary: e.target.value,
                              }))
                            }
                          />
                          <label htmlFor="current_salary">Salary</label>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className=" form-floating form-floating-outline ">
                          <input
                            type="number"
                            className="form-control"
                            name="current_overtime_rate"
                            id="current_overtime_rate"
                            placeholder="Overtime Rate (Per Hours)"
                            value={modalData?.current_overtime_rate}
                            onChange={(e) =>
                              setModalData((prev) => ({
                                ...prev,
                                current_overtime_rate: e.target.value,
                              }))
                            }
                          />
                          <label htmlFor="current_overtime_rate">
                            Overtime Rate (Per Hours)
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card border shadow-sm mb-4">
                  <div className="card-header bg-label-warning fw-semibold">
                    <i className="ri-calendar-check-line me-2"></i>
                    Leave Policy
                  </div>
                  <div className="card-body pt-4">
                    <div className="row g-3">
                      <div className="col-md-4">
                        <div className=" form-floating form-floating-outline ">
                          <input
                            type="number"
                            className="form-control"
                            name="paid_leaves_per_month"
                            id="paid_leaves_per_month"
                            placeholder="Paid Leaves Per Month"
                            value={modalData?.paid_leaves_per_month}
                            onChange={(e) =>
                              setModalData((prev) => ({
                                ...prev,
                                paid_leaves_per_month: e.target.value,
                              }))
                            }
                          />
                          <label htmlFor="paid_leaves_per_month">
                            Paid Leaves Per Month
                          </label>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className=" form-floating form-floating-outline">
                          <select
                            name="carry_forward"
                            id="carry_forward"
                            className="form-select"
                            value={modalData?.carry_forward}
                            onChange={(e) =>
                              setModalData((prev) => ({
                                ...prev,
                                carry_forward: e.target.value,
                              }))
                            }
                          >
                            <option value="2">No</option>
                            <option value="1">Yes</option>
                          </select>
                          <label htmlFor="carry_forward">
                            Paid Leaves Carry Forward
                          </label>
                        </div>
                      </div>
                      {modalData?.carry_forward == "1" && (
                        <div className="col-md-4">
                          <div className=" form-floating form-floating-outline ">
                            <input
                              type="number"
                              className="form-control"
                              name="max_carry_forward"
                              id="max_carry_forward"
                              placeholder=" Maximum Paid Leaves Carry Forward"
                              value={modalData?.max_carry_forward}
                              onChange={(e) =>
                                setModalData((prev) => ({
                                  ...prev,
                                  max_carry_forward: e.target.value,
                                }))
                              }
                            />
                            <label htmlFor="max_carry_forward">
                              Maximum Paid Leaves Carry Forward
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="card border shadow-sm">
                  <div className="card-header bg-label-info fw-semibold">
                    <i className="ri-file-text-line me-2"></i>
                    Remarks & Attachment
                  </div>
                  <div className="card-body pt-4">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <div className=" form-floating form-floating-outline ">
                          <textarea
                            type="text"
                            className="form-control input-uppercase"
                            name="remarks"
                            id="remarks"
                            placeholder="Remark"
                            value={modalData?.remarks}
                            onChange={(e) =>
                              setModalData((prev) => ({
                                ...prev,
                                remarks: e.target.value,
                              }))
                            }
                          />
                          <label htmlFor="remarks">Remark</label>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className=" form-floating form-floating-outline ">
                          <input
                            type="file"
                            className="form-control"
                            name="joining_img"
                            id="joining_img"
                          />
                          <label htmlFor="joining_img">Joining Later Img</label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer py-3 bg-label-primary">
                <button
                  type="button"
                  className="btn btn-secondary"
                  data-bs-dismiss="modal"
                >
                  Close
                </button>
                <button type="submit" className="btn btn-primary">
                  Save changes
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* ══════════════════ DAY-WISE SALARY PREVIEW MODAL ══════════════════ */}
      <div
        className="modal fade"
        id="staff_day_salary"
        tabIndex={-1}
        aria-labelledby="staffDaySalaryLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header py-3 bg-label-success">
              <h1 className="modal-title fs-5" id="staffDaySalaryLabel">
                <i className="ri-money-rupee-circle-line me-2"></i>
                Day-wise Salary — {daySalaryStaff?.name || ""}
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              <p className="text-muted small mb-3">
                Pick a date to see that day's attendance and earned amount for
                this staff member. This is a preview only — nothing is saved.
              </p>

              <div className="row g-2 align-items-end mb-3">
                <div className="col-8">
                  <div className="form-floating form-floating-outline">
                    <input
                      type="date"
                      className="form-control"
                      value={daySalaryDate}
                      max={new Date().toISOString().split("T")[0]}
                      onChange={(e) => setDaySalaryDate(e.target.value)}
                    />
                    <label>Date</label>
                  </div>
                </div>
                <div className="col-4">
                  <button
                    type="button"
                    className="btn btn-success w-100"
                    onClick={handleFetchDaySalary}
                    disabled={daySalaryLoading}
                  >
                    {daySalaryLoading ? (
                      "Checking..."
                    ) : (
                      <>
                        <i className="ri-search-line me-1"></i>Check
                      </>
                    )}
                  </button>
                </div>
              </div>

              {daySalaryError && (
                <div className="alert alert-warning py-2 small mb-0">
                  <i className="ri-error-warning-line me-1"></i>
                  {daySalaryError}
                </div>
              )}

              {!daySalaryError && daySalaryResult && (
                <div className="border rounded-3 p-3">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div>
                      <div className="fw-semibold">
                        {formatDate(daySalaryResult.date)}
                      </div>
                      <small className="text-muted">
                        {daySalaryResult.attendance_type || "—"}
                      </small>
                    </div>
                    {dayStatusBadge(daySalaryResult.status)}
                  </div>

                  <div className="row g-3 text-center mb-3">
                    <div className="col-6">
                      <div className="border rounded-3 py-2">
                        <small className="text-muted d-block">Check In</small>
                        <div className="fw-semibold">
                          {daySalaryResult.check_in
                            ? formatTime(daySalaryResult.check_in)
                            : "—"}
                        </div>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="border rounded-3 py-2">
                        <small className="text-muted d-block">Check Out</small>
                        <div className="fw-semibold">
                          {daySalaryResult.check_out
                            ? formatTime(daySalaryResult.check_out)
                            : "—"}
                        </div>
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="border rounded-3 py-2">
                        <small className="text-muted d-block">Worked</small>
                        <div className="fw-semibold">
                          {daySalaryResult.worked_hours ?? 0}h
                        </div>
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="border rounded-3 py-2">
                        <small className="text-muted d-block">Required</small>
                        <div className="fw-semibold">
                          {daySalaryResult.required_hours ?? 0}h
                        </div>
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="border rounded-3 py-2">
                        <small className="text-muted d-block">Overtime</small>
                        <div className="fw-semibold text-primary">
                          {daySalaryResult.overtime_hrs ?? 0}h
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex align-items-center justify-content-between bg-label-success rounded-3 p-3">
                    <div>
                      <small className="text-muted d-block">
                        {daySalaryResult.attendance_type === "Half Day"
                          ? "Half-day Amount"
                          : "Per-day Salary"}
                      </small>
                      <h5 className="fw-bold text-success mb-0">
                        {rupee(
                          daySalaryResult.attendance_type === "Half Day"
                            ? daySalaryResult.half_salary_earned
                            : daySalaryResult.per_day_salary,
                        )}
                      </h5>
                    </div>
                    <small className="text-muted">
                      Base per-day rate: {rupee(daySalaryResult.per_day_salary)}
                    </small>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer py-3">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
      {viewMenuStaff && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          style={{
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 1060,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setViewMenuStaff(null);
            }
          }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header bg-label-primary">
                <div>
                  <h5 className="modal-title fw-bold">
                    <i className="ri-menu-2-line me-2" />
                    Assigned Mobile Menus
                  </h5>

                  <small className="text-muted">
                    Menus assigned to {viewMenuStaff.name}
                  </small>
                </div>

                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setViewMenuStaff(null)}
                  aria-label="Close"
                />
              </div>

              <div className="modal-body">
                {hasAssignedMenus(viewMenuStaff.menu_group) ? (
                  viewMenuStaff.menu_group
                    .filter(
                      (group) =>
                        Array.isArray(group?.items) && group.items.length > 0,
                    )
                    .map((group, groupIndex) => (
                      <div
                        key={group.id ?? group.name ?? groupIndex}
                        className="border rounded-3 p-3 mb-3"
                      >
                        <h6 className="fw-semibold mb-3">
                          {group.title ?? group.name ?? group.label ?? "Menus"}
                        </h6>

                        <div className="d-flex flex-wrap gap-2">
                          {group.items.map((menu, menuIndex) => (
                            <span
                              key={menu.route ?? menuIndex}
                              className="badge bg-label-info rounded-pill px-3 py-2"
                            >
                              {menu.title ??
                                menu.name ??
                                menu.label ??
                                menu.route}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))
                ) : (
                  <p className="text-muted mb-0">
                    No mobile menus assigned to this staff member.
                  </p>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setViewMenuStaff(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
